/**
 * AlertExamples.tsx
 * Comprehensive examples of HeroUI Alert component
 * 
 * Showcases all HeroUI Alert features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Alert } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { 
  CheckCircle,
  AlertTriangle,
  Info,
  X,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  AlertCircle,
  Bell,
  Clock,
  Wifi,
  WifiOff
} from 'lucide-react';

export const AlertExamples: React.FC = () => {
  const [dismissibleAlerts, setDismissibleAlerts] = useState<Record<string, boolean>>({
    success: true,
    warning: true,
    danger: true,
    info: true,
    trading1: true,
    trading2: true,
    trading3: true,
    system1: true,
    system2: true
  });

  const dismissAlert = (id: string) => {
    setDismissibleAlerts(prev => ({ ...prev, [id]: false }));
  };

  const resetAlerts = () => {
    setDismissibleAlerts({
      success: true,
      warning: true,
      danger: true,
      info: true,
      trading1: true,
      trading2: true,
      trading3: true,
      system1: true,
      system2: true
    });
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Alert Colors</h3>
          <div className="space-y-4">
            <Alert
              color="default"
              title="Default Alert"
              description="This is a default alert for general information."
              startContent={<Info className="w-4 h-4" />}
            />
            
            <Alert
              color="primary"
              title="Primary Alert"
              description="This is a primary alert for important announcements."
              startContent={<Bell className="w-4 h-4" />}
            />
            
            <Alert
              color="secondary"
              title="Secondary Alert"
              description="This is a secondary alert for supplementary information."
              startContent={<Activity className="w-4 h-4" />}
            />
            
            <Alert
              color="success"
              title="Success Alert"
              description="Action completed successfully! Your changes have been saved."
              startContent={<CheckCircle className="w-4 h-4" />}
            />
            
            <Alert
              color="warning"
              title="Warning Alert"
              description="Please review your settings before proceeding with this action."
              startContent={<AlertTriangle className="w-4 h-4" />}
            />
            
            <Alert
              color="danger"
              title="Error Alert"
              description="An error occurred while processing your request. Please try again."
              startContent={<X className="w-4 h-4" />}
            />
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Alert Variants</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            <div>
              <h4 className="font-medium mb-3">Solid Variants</h4>
              <div className="space-y-3">
                <Alert
                  variant="solid"
                  color="success"
                  title="Solid Success"
                  description="Strong emphasis for important success messages"
                />
                <Alert
                  variant="solid"
                  color="warning"
                  title="Solid Warning"
                  description="High visibility for critical warnings"
                />
                <Alert
                  variant="solid"
                  color="danger"
                  title="Solid Error"
                  description="Maximum attention for errors"
                />
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Bordered Variants</h4>
              <div className="space-y-3">
                <Alert
                  variant="bordered"
                  color="success"
                  title="Bordered Success"
                  description="Clean success messages with defined boundaries"
                />
                <Alert
                  variant="bordered"
                  color="warning"
                  title="Bordered Warning"
                  description="Structured warnings with clear separation"
                />
                <Alert
                  variant="bordered"
                  color="danger"
                  title="Bordered Error"
                  description="Well-defined error messages"
                />
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Flat Variants</h4>
              <div className="space-y-3">
                <Alert
                  variant="flat"
                  color="success"
                  title="Flat Success"
                  description="Subtle success notifications"
                />
                <Alert
                  variant="flat"
                  color="warning"
                  title="Flat Warning"
                  description="Gentle warnings without overwhelming"
                />
                <Alert
                  variant="flat"
                  color="danger"
                  title="Flat Error"
                  description="Soft error messages"
                />
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Faded Variants</h4>
              <div className="space-y-3">
                <Alert
                  variant="faded"
                  color="success"
                  title="Faded Success"
                  description="Understated success messages"
                />
                <Alert
                  variant="faded"
                  color="warning"
                  title="Faded Warning"
                  description="Muted warnings for secondary information"
                />
                <Alert
                  variant="faded"
                  color="danger"
                  title="Faded Error"
                  description="Subdued error notifications"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Dismissible Alerts</h3>
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              <Button size="sm" onPress={resetAlerts}>Reset All Alerts</Button>
              <Badge color="primary" variant="flat">
                {Object.values(dismissibleAlerts).filter(Boolean).length} Active
              </Badge>
            </div>

            {dismissibleAlerts['success'] && (
              <Alert
                color="success"
                title="Trade Executed Successfully"
                description="Your order for 100 shares of AAPL has been filled at $150.25"
                startContent={<CheckCircle className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('success')}
              />
            )}

            {dismissibleAlerts['warning'] && (
              <Alert
                color="warning"
                title="Daily Loss Limit Approaching"
                description="You've reached 80% of your daily loss limit. Consider reviewing your positions."
                startContent={<AlertTriangle className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('warning')}
              />
            )}

            {dismissibleAlerts['danger'] && (
              <Alert
                color="danger"
                title="Connection Lost"
                description="Unable to connect to trading servers. Your orders may be delayed."
                startContent={<WifiOff className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('danger')}
              />
            )}

            {dismissibleAlerts['info'] && (
              <Alert
                color="primary"
                title="Market Update"
                description="The Federal Reserve has announced new interest rate decisions."
                startContent={<Info className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('info')}
              />
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading-Specific Alerts</h3>
          <div className="space-y-4">
            
            {dismissibleAlerts['trading1'] && (
              <Alert
                variant="bordered"
                color="success"
                title="Profitable Position Closed"
                description={
                  <div className="space-y-2">
                    <p>Your TSLA position has been closed with a profit of $1,234.56</p>
                    <div className="flex gap-2">
                      <Chip size="sm" color="success" variant="flat">+8.4% Return</Chip>
                      <Chip size="sm" color="primary" variant="flat">3 days hold</Chip>
                    </div>
                  </div>
                }
                startContent={<TrendingUp className="w-5 h-5" />}
                isClosable
                onClose={() => dismissAlert('trading1')}
              />
            )}

            {dismissibleAlerts['trading2'] && (
              <Alert
                variant="solid"
                color="warning"
                title="Risk Management Alert"
                description={
                  <div className="space-y-2">
                    <p>Position size exceeds 5% of portfolio. Consider reducing exposure.</p>
                    <div className="flex gap-2">
                      <Chip size="sm" color="warning" variant="flat">High Risk</Chip>
                      <Chip size="sm" color="default" variant="flat">NVDA - $12,450</Chip>
                    </div>
                  </div>
                }
                startContent={<AlertCircle className="w-5 h-5" />}
                isClosable
                onClose={() => dismissAlert('trading2')}
              />
            )}

            {dismissibleAlerts['trading3'] && (
              <Alert
                variant="flat"
                color="danger"
                title="Stop Loss Triggered"
                description={
                  <div className="space-y-2">
                    <p>Your AMD position was closed due to stop loss at $95.50</p>
                    <div className="flex gap-2">
                      <Chip size="sm" color="danger" variant="flat">-2.1% Loss</Chip>
                      <Chip size="sm" color="default" variant="flat">Risk Protected</Chip>
                    </div>
                  </div>
                }
                startContent={<TrendingDown className="w-5 h-5" />}
                isClosable
                onClose={() => dismissAlert('trading3')}
              />
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">System Status Alerts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {dismissibleAlerts['system1'] && (
              <Alert
                variant="bordered"
                color="success"
                title="All Systems Operational"
                description="Trading platform is running smoothly"
                startContent={<Wifi className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('system1')}
              />
            )}

            {dismissibleAlerts['system2'] && (
              <Alert
                variant="bordered"
                color="warning"
                title="Scheduled Maintenance"
                description="System maintenance in 30 minutes"
                startContent={<Clock className="w-4 h-4" />}
                isClosable
                onClose={() => dismissAlert('system2')}
              />
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Alert Radius Options</h3>
          <div className="space-y-4">
            <Alert
              radius="none"
              color="primary"
              title="No Radius"
              description="Sharp corners for modern, clean designs"
            />
            
            <Alert
              radius="sm"
              color="secondary"
              title="Small Radius"
              description="Subtle rounding for softer appearance"
            />
            
            <Alert
              radius="md"
              color="success"
              title="Medium Radius (Default)"
              description="Balanced rounding for most use cases"
            />
            
            <Alert
              radius="lg"
              color="warning"
              title="Large Radius"
              description="More pronounced rounding for friendly feel"
            />
            
            <Alert
              radius="full"
              color="danger"
              title="Full Radius"
              description="Maximum rounding for pill-shaped alerts"
            />
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Complex Alert Content</h3>
          <div className="space-y-4">
            
            <Alert
              variant="bordered"
              color="primary"
              title="Portfolio Performance Summary"
              description={
                <div className="space-y-3">
                  <p>Your portfolio has gained <strong>+12.4%</strong> this month across all positions.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardBody className="p-3">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-success" />
                          <div>
                            <p className="text-sm font-medium">Total P&L</p>
                            <p className="text-xs text-success">+$2,450.67</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody className="p-3">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium">Win Rate</p>
                            <p className="text-xs text-primary">76.3%</p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              }
              startContent={<TrendingUp className="w-5 h-5" />}
            />

            <Alert
              variant="solid"
              color="warning"
              title="Account Verification Required"
              description={
                <div className="space-y-3">
                  <p>To continue trading, please complete your account verification process.</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-warning-foreground" />
                      <span className="text-sm">Identity verification - Complete</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-warning-foreground" />
                      <span className="text-sm">Address verification - Complete</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warning-foreground" />
                      <span className="text-sm">Income verification - Pending</span>
                    </div>
                  </div>
                </div>
              }
              startContent={<AlertTriangle className="w-5 h-5" />}
            />
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Without Icons</h3>
          <div className="space-y-4">
            <Alert
              hideIcon
              color="success"
              title="Clean Success Message"
              description="Sometimes you want alerts without icons for a cleaner look"
            />
            
            <Alert
              hideIcon
              color="warning"
              title="Simple Warning"
              description="Icon-free alerts can work well in dense interfaces"
            />
            
            <Alert
              hideIcon
              variant="bordered"
              color="primary"
              title="Minimalist Design"
              description="Focus on the content without visual distractions"
            />
          </div>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};